import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import WideContent from 'core/layout/content/wide-content';
import Translate from 'ui/atoms/translate';
import WithAuthenticatedPage from 'core/auth/components/with-authenticated-page';
import { AppType } from 'core/auth/types';
import useApiCall from 'hooks/use-api-call';
import { AdminApi } from 'api/apis';
import LoadingRing from 'ui/atoms/loading-ring';
import { AdminInvestor, Identification, BenefitingPerson, LegalPerson } from 'api/models';
import BoxedContent from 'ui/molecules/boxed-content';
import StudioInvestmentInvestorDetails from 'src/apps/issuer/shared/studio-investment-investor-details';
import Section from 'ui/atoms/section';
import KycAndCompliance, { KycAndComplianceType } from 'apps/issuer/shared/kyc-and-compliance-view';
import UserData from './user-data-view';
import InvestmentsView from './investments-view';
import Link from 'core/routing/link';
import EmailHistoryView from './emails-history';
import Button from 'ui/atoms/button';
import * as Styled from './styled';
import DeleteAccountModal from './delete-account-modal';
import { useTranslateWithStringArgs } from 'ui/hooks/use-translate';
import moment from 'moment';
import ReactivationModal from './reactivation-modal';
import { PersonType } from 'ui/types/person';
import {
  hasDistributionManageDataPermission,
  hasDistributionManageIdentificationDataPermission,
} from 'apps/issuer/helpers/distributionPlatformPermissions';
import { useCurrentUserSelector } from 'core/auth/hooks';
import ActionButtons from 'src/ui/molecules/action-buttons';
import Tooltip, { TooltipPosition } from 'src/ui/atoms/tooltip';

interface IssuerInvestorDetailsPageProps {
  investorId: string;
}

const IssuerInvestorDetailsPage: FunctionComponent<IssuerInvestorDetailsPageProps> = ({ investorId }) => {
  const [investor, setInvestor] = useState<AdminInvestor>();
  const [benefitingPersons, setBenefitingPersons] = useState<BenefitingPerson[]>();
  const [identification, setIdentification] = useState<Identification>();
  const [isDeleteAccountModalOpen, setIsDeleteAccountModalOpen] = useState(false);
  const [isReactivationModalOpen, setIsReactivationModalOpen] = useState(false);
  const [isAccountDeactivated, setIsAccountDeactivated] = useState(false);
  const [createdAt, setCreatedAt] = useState('');
  const [earliestDeletionDatetime, setEarliestDeletionDatetime] = useState('');
  const [email, setEmail] = useState('');
  const [isComplianceExportRequired, setComplianceExportRequired] = useState(false);
  const [investorsDistributionPlatformName, setInvestorsDistributionPlatformName] = useState('');

  const { withApi, makeAuthenticatedApi } = useApiCall();

  const adminApi: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);

  const translate = useTranslateWithStringArgs();
  const { currentUser } = useCurrentUserSelector();

  const formatDate = (date: Date) => moment(date).format(`${translate('date.dateFormat')}`);

  const fetchInvestorDetails = useCallback(() => {
    withApi(async () => {
      const investorDetails = await adminApi.adminInvestorsRetrieve({ id: investorId });
      setInvestor(investorDetails);
      const identification = await adminApi.adminInvestorsIdentificationsBestCurrentRetrieve({ id: investorId });
      setIdentification(identification);
      if (investorDetails?.person.personType === PersonType.Legal) {
        const response = await adminApi.adminInvestorsBenefitingPersonsList({ id: investorId });
        if (response) {
          setBenefitingPersons(response);
        }
      }
    });
  }, [investorId]);

  const fetchInvestorDeletion = useCallback(() => {
    withApi(async () => {
      try {
        const response = await adminApi.adminInvestorsPendingDeletionRetrieve({ id: investorId });
        if (response) {
          const {
            createdAt,
            earliestDeletionDatetime,
            requestingUser: { email },
          } = response;

          setCreatedAt(formatDate(createdAt));
          setEarliestDeletionDatetime(formatDate(earliestDeletionDatetime));
          setEmail(email);
          setIsAccountDeactivated(true);
        }
      } catch (e) {
        setIsAccountDeactivated(false);
      }
    });
  }, [investorId]);

  const fetchInvestorDistributionPlatform = useCallback(() => {
    if (!investor?.distributionPlatformId) return;

    withApi(async () => {
      try {
        const { usesEffecta, displayName } = await adminApi.adminDistributionPlatformsRetrieve({
          id: investor.distributionPlatformId,
        });
        setComplianceExportRequired(usesEffecta);
        setInvestorsDistributionPlatformName(displayName);
      } catch (e) {
        console.error(e);
      }
    });
  }, [adminApi, investorId, investor?.distributionPlatformId]);

  useEffect(() => {
    if (!investor?.distributionPlatformId) {
      fetchInvestorDetails();
      fetchInvestorDeletion();
    } else {
      fetchInvestorDistributionPlatform();
    }
  }, [withApi, adminApi, investorId, investor?.distributionPlatformId]);

  const onDeleteModalClose = () => {
    setIsDeleteAccountModalOpen(false);
    fetchInvestorDeletion();
  };

  const onReactivationModalClose = () => {
    setIsReactivationModalOpen(false);
    fetchInvestorDeletion();
  };

  const restartIdentification = () => {
    if (!identification) return null;

    withApi(async () => {
      await adminApi.adminInvestorsIdentificationsRestartCreate({
        id: investorId,
        identificationRequest: {
          ...identification,
          legitimationProtocolId: identification.legitimationProtocol?.id || '',
        },
      });
      fetchInvestorDetails();
    });
  };

  const canManageData = hasDistributionManageDataPermission(
    currentUser?.associatedDistributionPlatforms?.filter(
      (platform) => platform.id === investor?.distributionPlatformId,
    ) || null,
  );

  const canAddIdentification = hasDistributionManageIdentificationDataPermission(
    currentUser?.associatedDistributionPlatforms?.filter(
      (platform) => platform.id === investor?.distributionPlatformId,
    ) || null,
  );

  const hasWallets = !!investor?.wallets?.length;

  return (
    <>
      <Section>
        <WideContent
          title={<Translate name="dashboardIssuerInvestorDetails.title" args={[investor && investor.investorName]} />}
          titleSpacing="larger"
        >
          {!investor && <LoadingRing />}
          {investor && identification && (
            <>
              <BoxedContent releaseSpaceWhenTitleIsEmpty={true} title={<Translate name="common.generalInformation" />}>
                <StudioInvestmentInvestorDetails
                  name={investor.investorName}
                  email={investor.email}
                  investorId={investor.id}
                  activeInvestmentsNo={investor.activeInvestmentsNo}
                  activeInvestmentsSum={investor.activeInvestmentsSum}
                  entryType={investor.entryType}
                  distributionPlatform={investorsDistributionPlatformName}
                  createdAt={createdAt}
                  earliestDeletionDatetime={earliestDeletionDatetime}
                  isAccountDeactivated={isAccountDeactivated}
                  requestingUserEmail={email}
                  onReactivateAccount={() => setIsReactivationModalOpen(true)}
                />
                {canManageData && !isAccountDeactivated && (
                  <ActionButtons>
                    {hasWallets ? (
                      <Tooltip
                        content={<Translate name="dashboardIssuerInvestorDetails.deleteAccount.errors.wallets" />}
                        position={TooltipPosition.BOTTOM}
                      >
                        <Button
                          disabled
                          variant="secondary"
                          color="danger"
                          onClick={() => setIsDeleteAccountModalOpen(true)}
                        >
                          <Translate name="dashboardIssuerInvestorDetails.deleteAccount.delete" />
                        </Button>
                      </Tooltip>
                    ) : (
                      <Button variant="secondary" color="danger" onClick={() => setIsDeleteAccountModalOpen(true)}>
                        <Translate name="dashboardIssuerInvestorDetails.deleteAccount.delete" />
                      </Button>
                    )}
                  </ActionButtons>
                )}
              </BoxedContent>
              <BoxedContent title={<Translate name="kycComplianceDetails.title" />} releaseSpaceWhenTitleIsEmpty={true}>
                <KycAndCompliance
                  type={KycAndComplianceType.INVESTOR}
                  kycStatus={investor.kycStatus}
                  hasCompliance={isComplianceExportRequired}
                  complianceExportRequired={isComplianceExportRequired}
                  comment={investor.compliance ? investor.compliance.comment : undefined}
                  complianceStatus={investor.compliance ? investor.compliance.status : undefined}
                  complianceDate={investor.compliance ? investor.compliance.complianceDate : undefined}
                  investorId={investor.id}
                  hasManageDataPermission={canManageData}
                  personData={investor.person as LegalPerson}
                  loadData={fetchInvestorDetails}
                  benefitingPersons={benefitingPersons}
                  hasFrontend={investor.distributionPlatform.hasFrontend}
                />
              </BoxedContent>
              <UserData
                investor={investor}
                benefitingPersons={benefitingPersons}
                canManage={canManageData}
                fetchInvestorDetails={fetchInvestorDetails}
                restartIdentification={restartIdentification}
                identification={identification}
                canAddIdentification={canAddIdentification}
              />
            </>
          )}
        </WideContent>
      </Section>
      {investor && (
        <>
          <InvestmentsView fetchInvestorDetails={fetchInvestorDetails} investorId={investorId} />
          <EmailHistoryView hasPermission={canManageData} investorId={investorId} />
        </>
      )}
      <WideContent>
        <Styled.SpaceBetweenContainer>
          <Link icon="arrow-left" to="/dashboard/investors">
            <Translate as="span" name="dashboardIssuerInvestorDetails.goToInvestors" />
          </Link>
        </Styled.SpaceBetweenContainer>
      </WideContent>

      {isDeleteAccountModalOpen && investor && (
        <DeleteAccountModal investor={investor} onModalClose={() => onDeleteModalClose()} />
      )}

      {isReactivationModalOpen && investor && (
        <ReactivationModal
          investor={investor}
          onModalClose={() => onReactivationModalClose()}
          onReactivationComplete={() => setIsAccountDeactivated(false)}
        />
      )}
    </>
  );
};

export default WithAuthenticatedPage(IssuerInvestorDetailsPage, AppType.ISSUER);
